import React, { useState, useEffect } from 'react';
import {
    Container, Typography, TextField, Button, Snackbar, Alert,
    Paper, Grid, Box, FormControl, InputLabel, Select, MenuItem,
    IconButton, Divider, Autocomplete, CircularProgress
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, SwapHoriz as SwapHorizIcon } from '@mui/icons-material';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { handleApiError } from '../utils/errorHandler';
import { useProductAutocomplete } from '../hooks/useAutocomplete';

const CreateStockTransfer = () => {
    const [stockTransfer, setStockTransfer] = useState({
        transferDate: new Date().toISOString().split('T')[0],
        fromLocation: '',
        toLocation: '',
        notes: ''
    });
    const [products, setProducts] = useState([{ productName: '', quantity: '' }]);
    const [availableLocations, setAvailableLocations] = useState([]);
    const [userLocation, setUserLocation] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    // Autocomplete hook
    const {
        options: productOptions,
        loading: productLoading,
        fetchSuggestions: fetchProducts
    } = useProductAutocomplete({ debounceMs: 300, minLength: 2 });

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        const location = localStorage.getItem('userLocation');
        setUserLocation(location);

        // Set default from location for non-Universal employees
        if (location && location !== 'Universal') {
            setStockTransfer(prev => ({ ...prev, fromLocation: location }));
        }

        fetchInitialData();
    }, [navigate]);

    const fetchInitialData = async () => {
        try {
            const locationsResponse = await api.get('/config/locations');
            const storageLocations = locationsResponse.data.storageLocations;
            setAvailableLocations(storageLocations);
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Failed to fetch initial data',
                severity: 'error'
            });
        }
    };

    const handleChange = (e) => {
        setStockTransfer({ ...stockTransfer, [e.target.name]: e.target.value });
    };

    const handleProductChange = (index, field, value) => {
        const updated = [...products];
        if (field === 'productName') {
            // value is the selected product object from Autocomplete
            updated[index].productName = value ? value.productName : '';
        } else if (field === 'quantity') {
            // Allow only decimal numbers
            if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                updated[index][field] = value;
            }
        }
        setProducts(updated);
    };

    const addProduct = () => {
        setProducts([...products, { productName: '', quantity: '' }]);
    };

    const removeProduct = (index) => {
        if (products.length > 1) {
            setProducts(products.filter((_, i) => i !== index));
        }
    };

    const validateForm = () => {
        if (!stockTransfer.fromLocation) {
            setSnackbar({ open: true, message: 'Source location is required', severity: 'error' });
            return false;
        }

        if (!stockTransfer.toLocation) {
            setSnackbar({ open: true, message: 'Destination location is required', severity: 'error' });
            return false;
        }

        if (stockTransfer.fromLocation === stockTransfer.toLocation) {
            setSnackbar({ open: true, message: 'Source and destination locations must be different', severity: 'error' });
            return false;
        }

        if (!stockTransfer.transferDate) {
            setSnackbar({ open: true, message: 'Transfer date is required', severity: 'error' });
            return false;
        }

        for (let i = 0; i < products.length; i++) {
            if (!products[i].productName) {
                setSnackbar({ open: true, message: `Product ${i + 1}: Product name is required`, severity: 'error' });
                return false;
            }
            if (!products[i].quantity || parseFloat(products[i].quantity) <= 0) {
                setSnackbar({ open: true, message: `Product ${i + 1}: Quantity must be greater than 0`, severity: 'error' });
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsLoading(true);
        try {
            const response = await api.post('/stock-transfers/create', {
                ...stockTransfer,
                products: products.map(p => ({
                    productName: p.productName,
                    quantity: parseFloat(p.quantity)
                }))
            });

            const stockUpdates = response.data.stockUpdates || [];
            const updatesText = stockUpdates.map(u =>
                `${u.productName}: ${u.fromLocation} (${u.fromNewStock}) -> ${u.toLocation} (${u.toNewStock})`
            ).join(', ');

            setSnackbar({
                open: true,
                message: `Stock Transfer created successfully! ${updatesText}`,
                severity: 'success'
            });

            // Reset form
            setStockTransfer({
                transferDate: new Date().toISOString().split('T')[0],
                fromLocation: userLocation && userLocation !== 'Universal' ? userLocation : '',
                toLocation: '',
                notes: ''
            });
            setProducts([{ productName: '', quantity: '' }]);

        } catch (error) {
            handleApiError(error, setSnackbar);
        } finally {
            setIsLoading(false);
        }
    };

    // Get available destination locations (exclude source)
    const getAvailableDestinations = () => {
        return availableLocations.filter(loc => loc !== stockTransfer.fromLocation);
    };

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper elevation={6} sx={{ p: 4, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <Typography
                        variant="h4"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                            color: 'info.main',
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1
                        }}
                    >
                        <SwapHorizIcon fontSize="large" />
                        Create Stock Transfer
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Move stock between inventory locations
                    </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Transfer Date"
                                name="transferDate"
                                type="date"
                                value={stockTransfer.transferDate}
                                onChange={handleChange}
                                variant="outlined"
                                required
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            {/* Spacer */}
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>From Location (Source)</InputLabel>
                                <Select
                                    name="fromLocation"
                                    value={stockTransfer.fromLocation}
                                    onChange={handleChange}
                                    label="From Location (Source)"
                                    disabled={userLocation && userLocation !== 'Universal'}
                                >
                                    {availableLocations.map((loc) => (
                                        <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>To Location (Destination)</InputLabel>
                                <Select
                                    name="toLocation"
                                    value={stockTransfer.toLocation}
                                    onChange={handleChange}
                                    label="To Location (Destination)"
                                    disabled={!stockTransfer.fromLocation}
                                >
                                    {getAvailableDestinations().map((loc) => (
                                        <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {!stockTransfer.fromLocation && (
                                <Typography variant="caption" color="text.secondary">
                                    Select source location first
                                </Typography>
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }}>
                                <Typography variant="overline" color="text.secondary">Products to Transfer</Typography>
                            </Divider>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" color="info.main">Products</Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={addProduct}
                                    size="small"
                                    sx={{ textTransform: 'none' }}
                                >
                                    Add Product
                                </Button>
                            </Box>
                        </Grid>

                        {products.map((product, index) => (
                            <React.Fragment key={index}>
                                <Grid item xs={12} sm={7}>
                                    <Autocomplete
                                        options={productOptions || []}
                                        loading={productLoading}
                                        value={product.productName ? { productName: product.productName } : null}
                                        onChange={(event, value) => handleProductChange(index, 'productName', value)}
                                        onInputChange={(event, value, reason) => {
                                            if (reason === 'input') {
                                                fetchProducts(value);
                                            }
                                        }}
                                        getOptionLabel={(option) => option?.productName || ''}
                                        isOptionEqualToValue={(option, value) => option?.productName === value?.productName}
                                        freeSolo={false}
                                        clearOnBlur={true}
                                        selectOnFocus={true}
                                        autoHighlight={true}
                                        disablePortal
                                        filterOptions={(x) => x}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={`Product ${index + 1}`}
                                                required
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {productLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    autoComplete: 'new-password',
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={8} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Quantity"
                                        type="text"
                                        value={product.quantity}
                                        onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                                        onFocus={(e) => e.target.select()}
                                        variant="outlined"
                                        required
                                        inputProps={{ inputMode: 'decimal' }}
                                    />
                                </Grid>
                                <Grid item xs={4} sm={1} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <IconButton
                                        onClick={() => removeProduct(index)}
                                        disabled={products.length === 1}
                                        color="error"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Grid>
                            </React.Fragment>
                        ))}

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Notes (optional)"
                                name="notes"
                                value={stockTransfer.notes}
                                onChange={handleChange}
                                multiline
                                rows={2}
                                variant="outlined"
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/stock-transfers')}
                            sx={{
                                px: 4,
                                py: 1.5,
                                textTransform: 'none',
                                fontWeight: 600
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="info"
                            sx={{
                                px: 4,
                                py: 1.5,
                                textTransform: 'none',
                                fontWeight: 600,
                                boxShadow: '0 4px 12px rgba(2, 136, 209, 0.3)',
                                '&:hover': {
                                    boxShadow: '0 6px 16px rgba(2, 136, 209, 0.4)',
                                }
                            }}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Creating...' : 'Create Stock Transfer'}
                        </Button>
                    </Box>
                </form>
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default CreateStockTransfer;
